import { useEffect, useState, useRef } from 'react';
import { motion, HTMLMotionProps } from 'motion/react';

const styles = {
     wrapper: {
          display: 'inline-block',
          whiteSpace: 'pre-wrap'
     },
     srOnly: {
          position: 'absolute' as 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          border: 0
     }
};

interface DecryptedTextProps extends HTMLMotionProps<'span'> {
     text: string;
     speed?: number;
     maxIterations?: number;
     sequential?: boolean;
     revealDirection?: 'start' | 'end' | 'center';
     useOriginalCharsOnly?: boolean;
     characters?: string;
     className?: string;
     parentClassName?: string;
     encryptedClassName?: string;
     animateOn?: 'view' | 'hover' | 'both';
     repeatOnView?: boolean;
}

export default function DecryptedText({
     text,
     speed = 100,
     maxIterations = 20,
     sequential = false,
     revealDirection = 'start',
     useOriginalCharsOnly = false,
     characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!@#$%^&*()_+',
     className = '',
     parentClassName = '',
     encryptedClassName = '',
     animateOn = 'hover',
     repeatOnView = false,
     ...props
}: DecryptedTextProps) {
     const [displayText, setDisplayText] = useState<string>(text);
     const [isHovering, setIsHovering] = useState<boolean>(false);
     const [isScrambling, setIsScrambling] = useState<boolean>(false);
     const [revealedIndices, setRevealedIndices] = useState<Set<number>>(new Set());
     const [hasAnimated, setHasAnimated] = useState<boolean>(false);
     const containerRef = useRef<HTMLSpanElement>(null);

     useEffect(() => {
          let interval: NodeJS.Timeout;
          let currentIteration = 0;

          const getNextIndex = (revealedSet: Set<number>): number => {
               const textLength = text.length;
               switch (revealDirection) {
                    case 'start':
                         return revealedSet.size;
                    case 'end':
                         return textLength - 1 - revealedSet.size;
                    case 'center': {
                         const middle = Math.floor(textLength / 2);
                         const offset = Math.floor(revealedSet.size / 2);
                         const nextIndex = revealedSet.size % 2 === 0 ? middle + offset : middle - offset - 1;

                         if (nextIndex >= 0 && nextIndex < textLength && !revealedSet.has(nextIndex)) {
                              return nextIndex;
                         }

                         for (let i = 0; i < textLength; i++) {
                              if (!revealedSet.has(i)) return i;
                         }
                         return 0;
                    }
                    default:
                         return revealedSet.size;
               }
          };

          const availableChars = useOriginalCharsOnly
               ? Array.from(new Set(text.split(''))).filter(char => char !== ' ')
               : characters.split('');

          const shuffleText = (originalText: string, currentRevealed: Set<number>): string => {
               if (useOriginalCharsOnly) {
                    const positions = originalText.split('').map((char, i) => ({
                         char,
                         isSpace: char === ' ',
                         index: i,
                         isRevealed: currentRevealed.has(i)
                    }));

                    const nonSpaceChars = positions.filter(p => !p.isSpace && !p.isRevealed).map(p => p.char);

                    for (let i = nonSpaceChars.length - 1; i > 0; i--) {
                         const j = Math.floor(Math.random() * (i + 1));
                         [nonSpaceChars[i], nonSpaceChars[j]] = [nonSpaceChars[j], nonSpaceChars[i]];
                    }

                    let charIndex = 0;
                    return positions
                         .map(p => {
                              if (p.isSpace) return ' ';
                              if (p.isRevealed) return originalText[p.index];
                              return nonSpaceChars[charIndex++];
                         })
                         .join('');
               } else {
                    return originalText
                         .split('')
                         .map((char, i) => {
                              if (char === ' ') return ' ';
                              if (currentRevealed.has(i)) return originalText[i];
                              return availableChars[Math.floor(Math.random() * availableChars.length)];
                         })
                         .join('');
               }
          };

          if (isHovering) {
               setIsScrambling(true);
               interval = setInterval(() => {
                    setRevealedIndices(prevRevealed => {
                         if (sequential) {
                              if (prevRevealed.size < text.length) {
                                   const nextIndex = getNextIndex(prevRevealed);
                                   const newRevealed = new Set(prevRevealed);
                                   newRevealed.add(nextIndex);
                                   setDisplayText(shuffleText(text, newRevealed));
                                   return newRevealed;
                              } else {
                                   clearInterval(interval);
                                   setIsScrambling(false);
                                   return prevRevealed;
                              }
                         } else {
                              setDisplayText(shuffleText(text, prevRevealed));
                              currentIteration++;
                              if (currentIteration >= maxIterations) {
                                   clearInterval(interval);
                                   setIsScrambling(false);
                                   setDisplayText(text);
                                   // mark animated so non-repeat behavior respects it
                                   setHasAnimated(true);
                              }
                              return prevRevealed;
                         }
                    });
               }, speed);
          } else {
               // when not hovering / not in view
               setDisplayText(text);
               setRevealedIndices(new Set());
               setIsScrambling(false);
          }

          return () => {
               if (interval) clearInterval(interval);
          };
     }, [isHovering, text, speed, maxIterations, sequential, revealDirection, characters, useOriginalCharsOnly]);

     useEffect(() => {
          if (animateOn !== 'view' && animateOn !== 'both') return;

          const observerOptions = {
               root: null,
               rootMargin: '0px',
               threshold: 0.15
          };

          const observer = new IntersectionObserver((entries) => {
               entries.forEach(entry => {
                    if (entry.isIntersecting) {
                         if (!hasAnimated || repeatOnView) {
                              setIsHovering(true);
                              if (!repeatOnView) {
                                   setHasAnimated(true);
                                   observer.disconnect();
                              }
                         }
                    } else {
                         // if repeat behavior is desired, stop scrambling when leaving viewport
                         if (repeatOnView) {
                              setIsHovering(false);
                         }
                    }
               });
          }, observerOptions);

          const currentRef = containerRef.current;
          if (currentRef) observer.observe(currentRef);

          return () => {
               if (currentRef) observer.unobserve(currentRef);
               observer.disconnect();
          };
     }, [animateOn, hasAnimated, repeatOnView]);

     const hoverProps =
          animateOn === 'hover' || animateOn === 'both'
               ? {
                    onMouseEnter: () => setIsHovering(true),
                    onMouseLeave: () => {
                         setIsHovering(false);
                         // ensure we don't block future "view" triggers if repeatOnView is enabled
                         if (!repeatOnView) setHasAnimated(true);
                    }
               }
               : {};

     useEffect(() => {
          // If text prop changes, reset so new text can animate on next trigger (unless non-repeat desired)
          setDisplayText(text);
          setRevealedIndices(new Set());
          if (!repeatOnView) setHasAnimated(false);
     }, [text, repeatOnView]);

     return (
          <motion.span className={parentClassName} ref={containerRef} style={styles.wrapper} {...hoverProps} {...props}>
               <span style={styles.srOnly}>{displayText}</span>

               <span aria-hidden="true">
                    {displayText.split('').map((char, index) => {
                         const isRevealedOrDone = revealedIndices.has(index) || !isScrambling || !isHovering;

                         return (
                              <span key={index} className={isRevealedOrDone ? className : encryptedClassName}>
                                   {char}
                              </span>
                         );
                    })}
               </span>
          </motion.span>
     );
}